// @flow
import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {colors} from '../../assets/styles/colors';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
} from '@material-ui/core';
import {getProductItemPricing} from '../../utils/product';

import {commaFormat} from '../../utils/number';
import {getImageUrl} from '../../utils/image_provider';
import {StarRounded, Warning} from '@material-ui/icons';
import Close from '@material-ui/icons/Close';
import {useIsFeatureInTier} from '../../utils/component/index';

import type {Currency, Product} from '../../entities';
import TiendadaTooltip from '../tiendada-tooltip';

type ProductCardType = 'CREATE' | 'EDIT';

type Props = {
  product?: Product,
  handleProductEnable?: (id: string, enable: boolean) => void,
  handleProductFeature?: (id: string, featured: boolean) => void,
  handleStockModalOpen?: (id: string, currentStock: number) => void,
  handleClick?: (p?: Product) => void,
  type?: ProductCardType,
  text?: string,
  currency?: Currency,
};
const useStyles = props =>
  makeStyles(theme => ({
    cardRoot: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
      marginRight: theme.spacing(3),
      // maxWidth: 248,
      height: 300,
      // maxWidth: 282,
      width: 300,
      boxShadow: 'unset',
      [theme.breakpoints.down('xs')]: {
        marginRight: theme.spacing(2),
        height: 240,
        width: 240,
      },
    },
    cardActionArea: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      height: '100%',
      borderRadius: '12px',
    },
    cardMedia: {
      position: 'absolute',
      borderRadius: '12px',
      height: '100%',
      opacity: props.product && !props.product.enabled ? 0.75 : 'initial',
      filter:
        props.product && !props.product.enabled ? 'grayscale(100%)' : null,
      transition: 'opacity 0.2s, filter 0.2s',
      // [theme.breakpoints.down('xs')]: {
      //   maxHeight: 200,
      // },
      display: 'flex',
      placeItems: 'center',
    },
    disabledAlert: {
      margin: theme.spacing(2),
      padding: 10,
      textAlign: 'center',
      backgroundColor: '#fff',
      borderRadius: '12px',
      opacity: props.product && !props.product.enabled ? 1 : 0,
      transition: 'opacity 0.2s',
    },
    disabledText: {
      fontSize: '0.875rem',
      // [theme.breakpoints.down('xs')]: {
      //   fontSize: '0.75rem',
      // },
    },
    cardContent: {
      position: 'absolute',
      // padding: 0,
      background: 'rgba(255, 255, 255, 0.85)',
      borderRadius: '10px',
      bottom: 0,
      width: 268,
      margin: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        // height: 240,
        width: 208,
      },
    },
    productTitle: {
      // lineHeight: theme.spacing(3),
      fontWeight: 500,
      // margin: theme.spacing(2, 0, 1, 0),
      opacity: props.product && !props.product.enabled ? 0.4 : 'initial',
      transition: 'opacity 0.2s',
      // height: theme.spacing(6),
      // whiteSpace: 'nowrap',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      'text-overflow': 'ellipsis',
    },
    productPrice: {
      marginTop: 4,
      // lineHeight: theme.spacing(4),
      opacity: props.product && !props.product.enabled ? 0.4 : 'initial',
      transition: 'opacity 0.3s',
      fontWeight: 600,
      fontSize: '1.375rem',
      // marginBottom: theme.spacing(2),
      // height: theme.spacing(6),
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      'text-overflow': 'ellipsis',
    },
    productRealPrice: {
      color: '#B8B8C7',
      fontWeight: 500,
      fontSize: '16px',
      textDecoration: 'line-through',
      marginLeft: '10px',
      marginTop: '11px',
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0,
      },
    },
    containerPrice: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'flex-start',
        flexDirection: 'column-reverse',
      },
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      height: 300,
      width: 300,
      border: '2px dashed #B8B8C7',
      opacity: 0.6,
      // boxSizing: 'borderBox',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 20,
      borderRadius: '12px',
      // '&:hover': {
      //   opacity: 1,
      // },
      transition: 'opacity 0.2s',
      // [theme.breakpoints.down('xs')]: {
      //   // maxWidth: '100%',
      //   minHeight: 272,
      // },
      marginRight: theme.spacing(3),
      [theme.breakpoints.down('xs')]: {
        height: 240,
        width: 240,
        marginRight: theme.spacing(2),
      },
    },
    starIcon: {
      backgroundColor: colors.grey200,
      color: colors.grey500,
      borderRadius: '30px',
      padding: 6,
      fontSize: '3rem',
    },
    agregarText: {
      fontSize: 14,
      margin: theme.spacing(1, 0),
    },
    featuredToggle: {
      position: 'absolute',
      right: 12,
      top: 12,
      background: 'rgba(255, 255, 255, 0.85)',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      '&:hover': {
        background: 'rgba(200, 200, 200, 0.85)',
      },
    },
    categoryBadge: {
      margin: theme.spacing(2),
      position: 'absolute',
      top: 0,
      left: 0,
      color: 'white',
      backgroundColor: colors.purple3,
      borderRadius: '10px',
      padding: '4px 12px',
      fontSize: '0.75rem',
    },
    warningAlert: {
      position: 'absolute',
      left: 12,
      top: 12,
      background: colors.warning,
      padding: 6,
      fontSize: '2rem',
      '&:hover': {
        color: colors.warning,
      },
    },
  }));

export default function FeaturedProductCard(props: Props) {
  const {
    product,
    currency,
    handleProductFeature,
    handleClick,
    type,
    text,
  } = props;
  const classes = useStyles(props)();

  // const hasTaxesFeature = useIsFeatureInTier('TAXES');

  const userHasProductEditFeature = useIsFeatureInTier('PRODUCT_EDIT');
  const userHasFeaturedProductsFeature = useIsFeatureInTier(
    'FEATURED_PRODUCTS'
  );

  /*   const productTaxFactor = useMemo(() => {
    if (hasTaxesFeature && product && product.taxes) {
      const taxes = product.taxes;
      return parseFloat(
        taxes.reduce((acc, curr) => acc + parseFloat(curr), 1).toFixed(2)
      );
    } else return 1;
  }, [hasTaxesFeature, product]); */

  const productPricing = useMemo(
    () => product && getProductItemPricing(product),
    [product]
  );

  const variantOptionsMissing = useMemo(() => {
    if (!product) return false;
    const variantSetting = product?.productVariantSetting;
    if (variantSetting) {
      return (
        variantSetting.enabled &&
        !variantSetting.variants &&
        !variantSetting.options
      );
    }
    return false;
  }, [product]);

  if (type === 'CREATE') {
    return (
      <Card classes={{root: classes.cardRoot}}>
        {/* <CardActionArea className={classes.cardActionArea}> */}
        {text && (
          <div className={classes.empty}>
            <StarRounded className={classes.starIcon} />
            <Typography className={classes.agregarText}>{text}</Typography>
          </div>
        )}
        {/* </CardActionArea> */}
      </Card>
    );
  }

  if (!product) {
    return <div className={classes.empty} />;
  }

  return (
    <Card
      className={classes.cardRoot}
      component={Link}
      to={`/products/${product.id}`}
    >
      {handleClick && (
        <CardActionArea
          className={classes.cardActionArea}
          style={
            !userHasProductEditFeature ? {pointerEvents: 'none'} : undefined
          }
          disabled={!userHasProductEditFeature}
          onClick={
            !userHasFeaturedProductsFeature
              ? () => handleClick(product)
              : undefined
          }
        >
          <CardMedia
            classes={{root: classes.cardMedia}}
            image={getImageUrl(product.mainImageId)}
            title={product.name}
          >
            <div className={classes.disabledAlert}>
              {variantOptionsMissing ? (
                <Typography className={classes.disabledText}>
                  El producto se ocultará en la tienda virtual por no tener
                  variantes configuradas
                </Typography>
              ) : (
                <Typography className={classes.disabledText}>
                  Este Producto no aparecerá en su tienda virtual
                </Typography>
              )}
            </div>
          </CardMedia>
          <CardContent className={classes.cardContent}>
            <Typography align="left" className={classes.productTitle}>
              {product.name}
            </Typography>
            <div className={classes.containerPrice}>
              {productPricing && productPricing.offerPrice ? (
                <>
                  <Typography className={classes.productPrice} align="left">
                    {commaFormat(
                      parseFloat(productPricing.offerPrice),
                      currency ? currency.symbol : ''
                    )}
                  </Typography>
                  <Typography
                    className={classes.productRealPrice}
                    align="right"
                  >
                    {commaFormat(
                      parseFloat(productPricing.price),
                      currency ? currency.symbol : ''
                    )}
                  </Typography>
                </>
              ) : (
                <Typography className={classes.productPrice} align="left">
                  {commaFormat(
                    productPricing?.price
                      ? parseFloat(productPricing?.price)
                      : '',
                    currency ? currency.symbol : ''
                  )}
                </Typography>
              )}
            </div>
          </CardContent>
        </CardActionArea>
      )}
      {userHasFeaturedProductsFeature && handleProductFeature && (
        <IconButton
          color="default"
          className={classes.featuredToggle}
          onClick={() => handleProductFeature(product.id, true)}
          size="small"
        >
          <Close />
        </IconButton>
      )}

      {variantOptionsMissing ? (
        <TiendadaTooltip
          title="Actualicé las variantes: no hay opciones disponibles."
          placement="top"
        >
          <IconButton
            color="default"
            className={classes.warningAlert}
            size="medium"
          >
            <Warning />
          </IconButton>
        </TiendadaTooltip>
      ) : (
        product.category && (
          <div className={classes.categoryBadge}>{product.category}</div>
        )
      )}
    </Card>
  );
}
